/**
 * AsciiField — a living chart rendered as numbers & math glyphs.
 * Canvas-based, no network. The price/NAV curves flow with time and bend
 * toward the cursor; the cursor also reveals/warps the hidden math field.
 * Ported verbatim from the Thema Console design.
 */

import { useEffect, useRef } from "react";

export function AsciiField() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const CELL = 14;
    const BG = "·+×∂Σπ√<>≈/=∫Δ%7301";
    const MID = "0123456789";
    const HOT = "08#Σ&%4216";

    let w = 0;
    let h = 0;
    let dpr = 1;
    let raf = 0;
    let t = 0;
    let last = performance.now();
    const m = { x: -9999, y: -9999, tx: -9999, ty: -9999 };

    const resize = () => {
      const r = canvas.parentElement!.getBoundingClientRect();
      if (!r.width || !r.height) return;
      dpr = Math.min(2, window.devicePixelRatio || 1);
      w = r.width;
      h = r.height;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      canvas.style.width = w + "px";
      canvas.style.height = h + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const onMove = (e: MouseEvent) => {
      const r = canvas.getBoundingClientRect();
      m.tx = e.clientX - r.left;
      m.ty = e.clientY - r.top;
    };
    const onLeave = () => {
      m.tx = -9999;
      m.ty = -9999;
    };

    const draw = () => {
      if (!w || !h) return;
      ctx.clearRect(0, 0, w, h);
      ctx.font = '500 11px "JetBrains Mono", monospace';
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";

      const accentRgb = (getComputedStyle(document.documentElement).getPropertyValue("--accent-rgb") || "37,99,235").trim();

      const pull = (x: number, base: number, strength: number) => {
        if (m.x < -1000) return base;
        const g = Math.exp(-((x - m.x) * (x - m.x)) / (2 * 150 * 150));
        return base + (m.y - base) * g * strength;
      };
      const priceY = (x: number) => {
        const base =
          h * 0.66 +
          Math.sin(x * 0.0045 + t * 0.7) * h * 0.085 +
          Math.sin(x * 0.011 - t * 0.45) * h * 0.045 +
          Math.sin(x * 0.023 + t) * h * 0.016;
        return pull(x, base, 0.38);
      };
      const navY = (x: number) => {
        const base = h * 0.685 + Math.sin(x * 0.004 + t * 0.55) * h * 0.05 + Math.sin(x * 0.009 - t * 0.3) * h * 0.024;
        return pull(x, base, 0.16);
      };

      const cols = Math.ceil(w / CELL);
      const rows = Math.ceil(h / CELL);
      for (let iy = 0; iy < rows; iy++) {
        for (let ix = 0; ix < cols; ix++) {
          const cx = ix * CELL + CELL / 2;
          const cy = iy * CELL + CELL / 2;
          const hv = (((ix + 7) * 73856093) ^ ((iy + 13) * 19349663)) >>> 0;
          const hf = (hv % 1000) / 1000;

          const dx = cx - m.x;
          const dy = cy - m.y;
          const d2 = dx * dx + dy * dy;
          const f = Math.exp(-d2 / (170 * 170 * 0.7));
          let drawX = cx;
          let drawY = cy;
          if (f > 0.01) {
            const d = Math.sqrt(d2) || 1;
            drawX += (dx / d) * f * 22;
            drawY += (dy / d) * f * 22;
          }

          const pY = priceY(cx);
          const nY = navY(cx);
          const dP = Math.abs(cy - pY);
          const dN = Math.abs(cy - nY);
          const iP = Math.exp(-(dP * dP) / (2 * 20 * 20));
          const iN = Math.exp(-(dN * dN) / (2 * 14 * 14));
          const lo = Math.min(pY, nY);
          const hi = Math.max(pY, nY);
          const inBand = cy > lo && cy < hi;

          const tick = Math.floor(t * 1.6 + hf * 7);
          let alpha: number;
          let color: string;
          let ch: string;

          if (iP > 0.18) {
            color = accentRgb;
            alpha = 0.28 + iP * 0.72;
            ch = HOT[(hv + tick) % HOT.length];
          } else if (iN > 0.22) {
            color = "90,105,128";
            alpha = 0.22 + iN * 0.55;
            ch = MID[(hv + tick) % MID.length];
          } else if (inBand) {
            if (hf < 0.3) continue;
            color = "14,156,144";
            alpha = 0.12 + 0.14 * hf;
            ch = MID[(hv + tick) % MID.length];
          } else {
            if (hf < 0.62 && f < 0.05) continue;
            color = f > 0.08 ? accentRgb : "71,85,105";
            alpha = 0.06 + hf * 0.05;
            ch = BG[(hv + Math.floor(t * 0.35 + hf * 5)) % BG.length];
          }

          alpha += f * 0.5;
          if (cy < h * 0.42 && cx < w * 0.66) alpha *= 0.4;
          if (cy < 40) alpha *= cy / 40;
          if (cy > h - 40) alpha *= (h - cy) / 40;

          ctx.fillStyle = `rgba(${color},${Math.min(0.95, alpha).toFixed(3)})`;
          ctx.fillText(ch, drawX, drawY);
        }
      }
    };

    const loop = (now: number) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      const still = reduced || document.body.classList.contains("mesh-still");
      if (!still) t += dt * 0.55;
      m.x += (m.tx - m.x) * 0.14;
      m.y += (m.ty - m.y) * 0.14;
      draw();
      raf = requestAnimationFrame(loop);
    };

    resize();
    draw();
    raf = requestAnimationFrame(loop);
    window.addEventListener("resize", resize);
    window.addEventListener("mousemove", onMove);
    document.addEventListener("mouseleave", onLeave);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  return <canvas ref={canvasRef} style={{ position: "absolute", inset: 0, display: "block" }} aria-hidden="true" />;
}
